import { useCallback, useLayoutEffect, useRef } from 'react';
import type {
  TextareaInsertionOptions,
  TextareaSelection,
} from '../types/textarea-insertion.types';

/**
 * Preserves native selection across insertion controls without moving focus.
 * All offsets remain UTF-16, matching the DOM API for joined emoji and tones.
 * @param options - Controlled draft, native editor and availability.
 * @returns Generic insertion, selection, focus and composition actions.
 */
export const useTextareaInsertion = ({
  inputRef,
  value,
  onChange,
  disabled,
}: TextareaInsertionOptions) => {
  const draft = useRef(value);
  const selection = useRef<TextareaSelection>({
    start: value.length,
    end: value.length,
  });
  const pendingSelection = useRef<TextareaSelection | undefined>(
    selection.current
  );
  const composing = useRef(false);

  useLayoutEffect(() => {
    draft.current = value;
    const next = pendingSelection.current;
    if (next) {
      inputRef.current?.setSelectionRange(next.start, next.end);
      pendingSelection.current = undefined;
    }
    selection.current = {
      start: Math.min(selection.current.start, value.length),
      end: Math.min(selection.current.end, value.length),
    };
  }, [inputRef, value]);

  const captureSelection = useCallback(() => {
    const input = inputRef.current;
    if (!input || disabled || composing.current) return false;
    // A selection scheduled by an insertion takes precedence over the old DOM.
    if (!pendingSelection.current) {
      selection.current = {
        start: input.selectionStart,
        end: input.selectionEnd,
      };
    }
    return true;
  }, [disabled, inputRef]);

  const insertText = useCallback(
    (text: string) => {
      if (disabled || composing.current || !inputRef.current) return false;
      const current = draft.current;
      const start = Math.min(selection.current.start, current.length);
      const end = Math.min(selection.current.end, current.length);
      const next = current.slice(0, start) + text + current.slice(end);
      selection.current = {
        start: start + text.length,
        end: start + text.length,
      };
      // An unchanged controlled value will not run the value layout effect.
      // Settle immediately only if the DOM already has this draft; otherwise
      // preserve the pending range for another insertion in the same batch.
      if (next === current && inputRef.current.value === next) {
        inputRef.current.setSelectionRange(
          selection.current.start,
          selection.current.end
        );
        pendingSelection.current = undefined;
      } else {
        pendingSelection.current = selection.current;
      }
      draft.current = next;
      onChange(next);
      return true;
    },
    [disabled, inputRef, onChange]
  );

  const focus = useCallback(() => {
    const input = inputRef.current;
    if (!input || disabled) return;
    input.focus({ preventScroll: true });
    input.setSelectionRange(selection.current.start, selection.current.end);
  }, [disabled, inputRef]);

  const setCursorPosition = useCallback(
    (position: number) => {
      const next = Math.max(0, position);
      selection.current = { start: next, end: next };
      if (next > draft.current.length) {
        pendingSelection.current = selection.current;
      }
      focus();
    },
    [focus]
  );

  return {
    captureSelection,
    insertText,
    focus,
    setCursorPosition,
    isComposing: () => composing.current,
    onCompositionStart: () => {
      composing.current = true;
    },
    onCompositionEnd: () => {
      composing.current = false;
      captureSelection();
    },
  };
};
